import { Currency } from '@mobi/utils/money'
import type { BetSlipBetsState } from '@mobi/betslip/Store/Bets'
import type { BetSlipItem, FobBetReceiptResponse, MultiInvestment } from '@mobi/betslip/types'
import {
  getBetsInMulti,
  getBetsToPlace,
  hasTooFewMultiLegs,
  hasTooManyMultiLegs,
  isFatalErrorType,
} from '../state'
import { calculateCombinedMultiInvestment, calculateMultiProjectedPay } from './multis'
import {
  calculateBetCostSingleItem,
  calculateBetCostSingleItemFromReceipt,
  calculateEstReturnForSingles,
  calculateEstReturnForSinglesReceipt,
} from './singles'

export const calculateTotalStake = (
  betslipItems: BetSlipItem[],
  multiInvestment: MultiInvestment,
  multiError: BetSlipBetsState['multiBetError'] | null
): number => {
  const combinedSingleCost = getBetsToPlace(betslipItems).reduce(
    (total, item) => total.add(calculateBetCostSingleItem(item)),
    new Currency(0)
  )

  const multiItems = getBetsInMulti(betslipItems)
  const isMultiInvalid =
    hasTooFewMultiLegs(multiItems) ||
    hasTooManyMultiLegs(multiItems) ||
    multiError?.betErrorType === 'DuplicateBonusBet' ||
    isFatalErrorType(multiError?.betErrorType) ||
    multiError?.betErrorType === 'Unspecified'

  if (isMultiInvalid) {
    return combinedSingleCost.value
  }

  const multiCost = calculateCombinedMultiInvestment(multiItems, multiInvestment)
  return combinedSingleCost.add(multiCost).value
}

export const calculateTotalStakeReceipt = (
  singleItems: BetSlipItem[],
  multiReceipt: FobBetReceiptResponse | undefined
) => {
  const combinedSingleCost = singleItems.reduce(
    (total, item) => total.add(calculateBetCostSingleItemFromReceipt(item)),
    new Currency(0)
  )
  return new Currency(combinedSingleCost).add(multiReceipt?.winInvestment || 0).value
}

export function calculateEstReturn(
  placeableItems: BetSlipItem[],
  placeableMultis: BetSlipItem[],
  multiInvestment: MultiInvestment
): number {
  const singlesEstRtn = calculateEstReturnForSingles(placeableItems) || 0
  const multiAndFormulaEstRtn = calculateMultiProjectedPay(placeableMultis, multiInvestment)
  return new Currency(singlesEstRtn).add(multiAndFormulaEstRtn).value
}

export function calculateReceiptReturn(
  placeableItems: BetSlipItem[],
  multiReceipt: FobBetReceiptResponse | undefined
): number {
  const singlesEstRtn = calculateEstReturnForSinglesReceipt(placeableItems) || 0
  return new Currency(singlesEstRtn).add(multiReceipt?.projectedWinPayout || 0).value
}
